import type { ReactElement } from 'react';
import { useMemo } from 'react';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaImportedEventHit } from '#/modules/search/algolia/types/SearchAlgoliaImportedEventHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import { getImportedListingUrl } from '#/modules/search/helpers/getImportedListingUrl';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';

import { SearchImportedHitInfo } from './SearchImportedHitInfo';

type Props = {
  hit: SearchAlgoliaImportedEventHit;
  trackClick: (() => void) | null;
  showLabel: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions: ReactElement | null;
};

export function SearchImportedHit({
  hit,
  trackClick,
  showLabel,
  highlighted,
  application,
  actions,
}: Props) {
  const { i18n } = useI18n();

  const {
    name,
    city,
    state,
    country,
    url,
    startDate,
    startTime,
    endDate,
    endTime,
    endsLocal,
    startsLocal,
    timezone,
    orgType,
    source,
    locationType,
    remoteZone,
    logo,
    imageUrl,
    imageHandle,
    isPostedAnonymously,
  } = hit;

  const imageHandleImage = imageHandle ? { handle: imageHandle } : null;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteZone, city, country, state],
  );

  const { orgName } = hit;

  const startDateTime = startDate && startTime ? startsLocal : null;
  const endDateTime = endDate && endTime ? endsLocal : null;

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link',
            href: getImportedListingUrl(url),
            target: '_blank',
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile>
            <SearchHitImage
              isImported
              imageUrl={imageUrl}
              image={imageHandleImage}
              source={source}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchImportedHitInfo
            application={application}
            endDateTime={endDateTime}
            isPostedAnonymously={isPostedAnonymously}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            showLabel={showLabel}
            startDateTime={startDateTime}
            timezone={timezone}
            source={source}
          />
        </SearchHitInteractiveContentWrapper>
      </SearchHitCard>
    </SearchHitHolder>
  );
}
